if (!customElements.get('sokynd-cart-drawer-extras')) {
  customElements.define('sokynd-cart-drawer-extras', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.addEventListener('submit', (event) => {
        const form = event.target.closest('.sokynd-cart-accessory__form');
        if (!form || !this.contains(form) || typeof window.fetch !== 'function') return;
        event.preventDefault();
        this.addAccessory(form);
      }, { signal: this.events.signal });
      this.addEventListener('change', (event) => {
        const select = event.target.closest('[data-accessory-variant]');
        if (!select || !this.contains(select)) return;
        const card = select.closest('.sokynd-cart-accessory');
        const option = select.options[select.selectedIndex];
        if (!option) return;
        card.querySelector('[data-accessory-price]').textContent = option.dataset.price;
        const compare = card.querySelector('[data-accessory-compare]');
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
      }, { signal: this.events.signal });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-refresh-cart]')) this.retryRefresh();
      }, { signal: this.events.signal });
    }

    cartTargets() {
      return ['page-header', 'cart-drawer'].map((selector) => document.querySelector(selector))
        .filter((target) => target?.dataset.sectionId && typeof target.updateFromCartChange === 'function');
    }

    waitForCartHook(variantId) {
      let complete;
      const promise = new Promise((resolve) => { complete = resolve; });
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        window.removeEventListener('SCE:add', receive);
        complete();
      };
      const receive = (event) => {
        if (String(event.detail?.variant_id) === String(variantId)) finish();
      };
      const timeout = setTimeout(finish, 1200);
      window.addEventListener('SCE:add', receive);
      return { promise, finish };
    }

    async mergeCartSections(sections, targets = this.cartTargets()) {
      let contents = sections || {};
      if (targets.some((target) => typeof contents[target.dataset.sectionId] !== 'string')) {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', targets.map((target) => target.dataset.sectionId).join(','));
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(this.dataset.refreshError);
        contents = await response.json();
      }
      if (targets.some((target) => typeof contents[target.dataset.sectionId] !== 'string')) {
        throw new Error(this.dataset.refreshError);
      }
      targets.forEach((target) => target.updateFromCartChange(contents[target.dataset.sectionId]));
    }

    setRefreshError() {
      this.needsRefresh = true;
      const status = this.querySelector('[data-extras-status]');
      if (status) {
        status.querySelector('[data-extras-status-message]').textContent = this.dataset.refreshError;
        status.hidden = false;
      }
    }

    async retryRefresh() {
      if (this.pending) return;
      this.pending = true;
      const retryButton = this.querySelector('[data-refresh-cart]');
      if (retryButton) retryButton.disabled = true;
      try {
        await this.mergeCartSections();
        this.needsRefresh = false;
        const status = this.querySelector('[data-extras-status]');
        if (status) status.hidden = true;
      } catch (error) {
        this.setRefreshError();
      } finally {
        this.pending = false;
        this.querySelectorAll('[name="add"]').forEach((button) => { button.disabled = !!this.needsRefresh; });
        const currentRetryButton = this.querySelector('[data-refresh-cart]');
        if (currentRetryButton) currentRetryButton.disabled = false;
      }
    }

    async addAccessory(form) {
      if (this.pending || this.needsRefresh) return;
      this.pending = true;
      const button = form.querySelector('[name="add"]');
      const status = form.closest('.sokynd-cart-accessory').querySelector('[data-accessory-error]');
      const originalLabel = button.textContent;
      this.querySelectorAll('[name="add"]').forEach((add) => { add.disabled = true; });
      button.textContent = this.dataset.adding;
      status.hidden = true;
      status.textContent = '';
      const data = new FormData(form);
      const targets = this.cartTargets();
      data.set('sections_url', window.location.pathname);
      data.set('sections', targets.map((target) => target.dataset.sectionId).join(','));
      const hook = this.waitForCartHook(data.get('id'));
      let added = false;
      try {
        const fallbackUrl = new URL(form.action, window.location.href);
        fallbackUrl.pathname = `${fallbackUrl.pathname.replace(/\.js$/, '')}.js`;
        const response = await fetch(window.theme?.routes?.cartAdd || fallbackUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        }
        added = true;
        await hook.promise;
        await this.mergeCartSections(result.sections, targets);
        this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: result.variant_id } }));
      } catch (error) {
        hook.finish();
        if (added) {
          this.setRefreshError();
        } else {
          status.textContent = error.message || this.dataset.error;
          status.hidden = false;
          this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: status.textContent } }));
        }
      } finally {
        button.textContent = originalLabel;
        this.querySelectorAll('[name="add"]').forEach((add) => { add.disabled = !!this.needsRefresh; });
        this.pending = false;
      }
    }

    disconnectedCallback() {
      this.events?.abort();
    }
  });
}
